import { FC, useEffect, useMemo, useRef, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";

import {
  Grid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import ReactDOM from "react-dom";
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import ContractExpirationFilterCell from "./components/ContractExpirationFilterCell";
import TagFilterCell from "./components/TagFilterCell";
import { registerDateChangeCallback } from "./helper/dateFilterCallbackStore";
import CheckboxFilterCell from "./components/CheckboxFilterCell";

type AccountWithTagsResponseDTO = {
  acctId: string;
  name: string;
  dialerType?: string[];
  noAutoRenew: boolean;
  contractExpiration?: string;
  termCommitment?: boolean;
  tags?: string[];
};

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: any) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};
interface PageState {
  skip: number;
  take: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const initialPage: PageState = { skip: 0, take: 20 };

// Custom filter function for tags that supports array contains operation
const customFilterFunction = (
  item: AccountWithTagsResponseDTO,
  filter: FilterDescriptor | CompositeFilterDescriptor,
): boolean => {
  if (!filter) {
    return true;
  }

  // Handle composite filters
  if ("logic" in filter && filter.filters) {
    if (filter.logic === "and") {
      return filter.filters.every((f) => customFilterFunction(item, f));
    } else {
      return filter.filters.some((f) => customFilterFunction(item, f));
    }
  }

  // Handle regular filters for tags
  const filterDescriptor = filter as FilterDescriptor;
  if (
    filterDescriptor.field === "tags" &&
    filterDescriptor.operator === "contains"
  ) {
    const value = filterDescriptor.value;
    // If the item has tags array, check if it contains the value
    if (item.tags && Array.isArray(item.tags)) {
      return item.tags.some((tag: string) =>
        tag.toLowerCase().includes(String(value).toLowerCase()),
      );
    }
    return false;
  }

  // Let the regular filtering handle non-tag fields
  return true;
};

const AccountsList: FC = () => {
  const [accounts, setAccounts] = useState<AccountWithTagsResponseDTO[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const gridRef = useRef(null);
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilter);
  const [page, setPage] = useState<PageState>(initialPage);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const loadData = async () => {
    setShowLoading(true);
    try {
      const response = await callApi(
        "sysadmin/accounts/GetAccounts",
        HttpMethod.POST,
        {},
      );
      setAccounts(response.data);
      setShowLoading(false);
    } catch (error) {
      console.error("Error fetching GetAccounts:", error);
      alert(`Error fetching data from GetAccounts: ${error}`);
      setShowLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    registerDateChangeCallback(({ filterType, startDate, endDate }) => {
      console.log("Filter from child:", filterType, startDate, endDate);
      if (filterType == "custom") {
        const customFilter: CompositeFilterDescriptor = {
          logic: "and",
          filters: [
            { field: "contractExpiration", operator: "gte", value: startDate },
            { field: "contractExpiration", operator: "lte", value: endDate },
          ],
        };
        setFilter(customFilter);
      }
    });
  }, []);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? accounts.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  // Apply custom filtering for tags
  const filteredData = useMemo(() => {
    if (!filter || !filter.filters || filter.filters.length === 0) {
      return accounts;
    }

    // Check if we have any tag filters
    const hasTagFilter = filter.filters.some((f) => {
      // For simple filter descriptors
      if ("field" in f && f.field === "tags") return true;

      // For composite filters (multiple tag selection)
      if ("logic" in f && f.filters && f.filters.length > 0) {
        return f.filters.some((subF: any) => subF.field === "tags");
      }

      return false;
    });

    if (hasTagFilter) {
      // Remove the tag filter and apply custom logic
      // then apply the kendo filter

      const filterWithoutTagFilter = { ...filter };
      filterWithoutTagFilter.filters = filterWithoutTagFilter.filters.filter(
        (f: any) => f.field !== "tags",
      );

      // Apply custom filtering for tags
      return filterBy(
        accounts.filter((item) => {
          // Find the tag filter
          const tagFilter = filter.filters.find(
            (f): f is FilterDescriptor =>
              "field" in f && f.field === "tags" && "operator" in f,
          );

          if (tagFilter) {
            if (Array.isArray(tagFilter.value)) {
              // Check if the item's tags array contains any of the filter values
              return tagFilter.value.some((filterTag: string) =>
                item.tags?.some((itemTag: string) =>
                  itemTag.toLowerCase().includes(filterTag.toLowerCase()),
                ),
              );
            } else if (typeof tagFilter.value === "string") {
              // Check if the item's tags array contains the single filter value
              return item.tags?.some((itemTag: string) =>
                itemTag.toLowerCase().includes(tagFilter.value.toLowerCase()),
              );
            }
          }
          return true;
        }),
        filterWithoutTagFilter,
      );
    } else {
      // Use standard filterBy for non-tag filters
      return filterBy(accounts, filter);
    }
  }, [accounts, filter]);

  const pagedData = useMemo(() => {
    const state: {
      skip: number;
      take: number;
    } = {
      skip: page.skip ?? 0,
      take: page.take ?? 20,
    };

    return filteredData.slice(state.skip, state.skip + state.take);
  }, [filteredData, page]);

  // Create a custom tag filter cell with access to accounts data
  const TagsFilterCell = (props: any) => (
    <TagFilterCell {...props} data={accounts} />
  );
  const AcctIdCell: FC<any> = (props) => {
    const acctId = props.dataItem[props.field];
    const targetUrl = `${runtimeConfig.classicUrl}/app/sysadmin/accounts.adp?acctId=${acctId}`;

    return (
      <td>
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#007bff",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          {acctId}
        </a>
      </td>
    );
  };

  return (
    <>
      {/* uncomment for debugging {filter && (
        <div className="alert alert-info">
          Filter for debugging: {JSON.stringify(filter)}
        </div>
      )} */}
      <div ref={gridRef} style={{ padding: "5px" }}>
        {showLoading ? <LoadingPanel gridRef={gridRef} /> : null}
        <Grid
          className="sysadmin-grid"
          data={pagedData}
          dataItemKey="acctId"
          filterable={true}
          sortable={true}
          skip={page.skip}
          take={page.take}
          total={filteredData.length}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 30, 40],
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          filter={filter}
          onFilterChange={handleFilterChange}
        >
          <Column field="acctId" title="Account ID" cell={AcctIdCell} />
          <Column field="name" title="Account Name" width="240px" />
          <Column
            field="dialerType"
            title="Dialer"
            filterCell={(props) => (
              <CheckboxFilterCell {...props} data={accounts} />
            )}
          />
          <Column
            field="noAutoRenew"
            width="180px"
            title="No Auto Renew"
            filter="boolean"
          />
          <Column
            field="contractExpiration"
            title="Contract Expiration"
            width="220px"
            filter="date"
            format="{0:d}"
            filterCell={ContractExpirationFilterCell}
          />
          <Column
            field="termCommitment"
            width="180px"
            title="Term Commitment"
            filter="boolean"
          />
          <Column field="tags" title="Tags" filterCell={TagsFilterCell} />
        </Grid>
      </div>
    </>
  );
};

export default AccountsList;
